import React from 'react';
import {
  Box, Typography, Paper, Button, Divider,
  TextField, Alert, Chip, CircularProgress,
  Radio, RadioGroup, FormControlLabel, Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ANESTHETIC_SECTIONS, MH_QUESTIONS } from '../../constants/ascForms';

// ─── Shared ASC patient-facing form panels ────────────────────────────────────
//
// Each component follows the portal interaction pattern:
//   data        — current form data object { [fieldKey]: value }
//   isComplete  — form has been submitted at least once (completedAt is set)
//   isEditing   — locked form has been unlocked by clicking Edit
//   onStartEdit — callback to unlock the form
//   saving      — show saving spinner
//   savedOk     — show "Saved ✓" chip briefly after save
//   saveError   — show error alert string
//   onSubmit    — called when patient/staff clicks Submit / Save Changes
//
// The flowboard wraps each component and renders its own staff-only
// extensions (notes, plans, provider signing slots) below the shared panel.
// Staff-only forms that have no patient component also live in this file.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Anesthetic Questionnaire ─────────────────────────────────────────────────
export function AnestheticQForm({
  data, isComplete, isEditing, onStartEdit,
  saving, savedOk, saveError,
  onUpdateAnswer, onUpdateSubField, onSubmit,
  staffReadOnly = false, onMarkComplete,
}) {
  const isAnswered = (q) => {
    const v = data[q.key];
    if (v === undefined || v === null || v === '') return false;
    if (typeof v === 'object') return v.answer !== undefined && v.answer !== null;
    return true;
  };
  const allAnswered = ANESTHETIC_SECTIONS.flatMap(s => s.questions).every(isAnswered);
  const patientDone = isComplete && !isEditing;
  const locked = staffReadOnly || patientDone;

  return (
    <Paper elevation={1} sx={{ p: 3, border: patientDone ? '2px solid #66bb6a' : '1px solid #e0e0e0' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {patientDone && <CheckCircleIcon sx={{ color: 'success.main' }} />}
          <Typography variant="h6" fontWeight={700}>Anesthetic Questionnaire</Typography>
          {saving && <CircularProgress size={16} />}
          {savedOk && <Chip label="Saved ✓" size="small" color="success" />}
        </Stack>
        {patientDone && !staffReadOnly && (
          <Button size="small" variant="outlined" onClick={onStartEdit}>Edit</Button>
        )}
        {staffReadOnly && !patientDone && onMarkComplete && Object.keys(data).length > 0 && (
          <Button size="small" variant="outlined" color="success" onClick={onMarkComplete}>
            Mark as Submitted by Patient
          </Button>
        )}
      </Stack>

      {patientDone ? (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1, mb: 2 }}>
          This form has been submitted. Click <strong>Edit</strong> above to make changes.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Please answer all questions honestly. This information helps your anesthesia team provide the safest care.
        </Typography>
      )}

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      {ANESTHETIC_SECTIONS.map((section) => (
        <Box key={section.key} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{
            mb: section.subtitle ? 0 : 1, color: 'text.secondary',
            textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5,
          }}>
            {section.title}
          </Typography>
          {section.subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontStyle: 'italic' }}>
              {section.subtitle}
            </Typography>
          )}
          {section.questions.map((q, qIdx) => {
            const answer = data[q.key];
            const isYes = answer === true || answer?.answer === true;
            return (
              <Box
                key={q.key}
                sx={{
                  py: 1.25, px: 1.5,
                  borderBottom: qIdx < section.questions.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: isYes ? '#fff8e1' : 'transparent',
                  borderRadius: 1,
                  opacity: locked ? 0.85 : 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <RadioGroup
                    row
                    value={answer === true || answer?.answer === true ? 'yes' : (answer === false || answer?.answer === false) ? 'no' : ''}
                    onChange={(e) => {
                      if (locked) return;
                      const val = e.target.value === 'yes';
                      if (q.type === 'yesno_explain' || q.subFields) {
                        onUpdateAnswer(q.key, { answer: val, explain: typeof answer === 'object' ? (answer?.explain || '') : '' });
                      } else {
                        onUpdateAnswer(q.key, val);
                      }
                    }}
                    sx={{ flexShrink: 0, mt: -0.25 }}
                  >
                    <FormControlLabel value="yes" disabled={locked} control={<Radio size="small" sx={{ py: 0.25 }} />} label={<Typography variant="caption" fontWeight={600}>Yes</Typography>} sx={{ mr: 1 }} />
                    <FormControlLabel value="no"  disabled={locked} control={<Radio size="small" sx={{ py: 0.25 }} />} label={<Typography variant="caption" fontWeight={600}>No</Typography>} />
                  </RadioGroup>
                  <Typography variant="body2" sx={{ pt: 0.25, lineHeight: 1.5 }}>{q.text}</Typography>
                </Box>

                {q.type === 'yesno_explain' && isYes && (
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    placeholder="Please explain..."
                    value={typeof answer === 'object' ? (answer?.explain || '') : ''}
                    onChange={(e) => onUpdateAnswer(q.key, { answer: true, explain: e.target.value })}
                    sx={{ mt: 1, ml: 6 }}
                    disabled={locked}
                  />
                )}

                {q.subFields && isYes && (
                  <Box sx={{ mt: 1, ml: 6, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {q.subFields.map(sf => (
                      <TextField
                        key={sf.key}
                        size="small"
                        label={sf.label}
                        value={typeof answer === 'object' ? (answer?.[sf.key] || '') : ''}
                        onChange={(e) => onUpdateSubField(q.key, sf.key, e.target.value)}
                        sx={{ minWidth: 200, flex: 1 }}
                        disabled={locked}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Please list any other medical problems not mentioned above
      </Typography>
      <TextField
        size="small" fullWidth multiline minRows={2}
        placeholder="Other medical problems..."
        value={data['_other_medical'] || ''}
        onChange={(e) => onUpdateAnswer('_other_medical', e.target.value)}
        disabled={locked}
        sx={{ mb: 2.5 }}
      />

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Please list previous surgeries and hospital admissions or visits below
      </Typography>
      <TextField
        size="small" fullWidth multiline minRows={3}
        placeholder="Year — Surgery or Hospital Admission..."
        value={data['_previous_surgeries'] || ''}
        onChange={(e) => onUpdateAnswer('_previous_surgeries', e.target.value)}
        disabled={locked}
        sx={{ mb: 2.5 }}
      />

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Do you have any questions about your anesthetic?
      </Typography>
      <TextField
        size="small" fullWidth multiline minRows={2}
        placeholder="Questions for the anesthesiologist..."
        value={data['_questions_about_anesthetic'] || ''}
        onChange={(e) => onUpdateAnswer('_questions_about_anesthetic', e.target.value)}
        disabled={locked}
        sx={{ mb: 2.5 }}
      />

      {!locked && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" disabled={saving || !allAnswered} onClick={onSubmit}>
              {isComplete ? 'Save Changes' : 'Submit Form'}
            </Button>
            {!allAnswered && (
              <Typography variant="caption" color="text.secondary">Please answer all questions before submitting.</Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}

// Normalizes an MH answer value — answers may be plain boolean, integer (legacy),
// or { answer: bool, explain: string } objects (yesno_explain format).
export const isMhYes = (v) => v === true || v === 1 || (typeof v === 'object' && v !== null && v.answer === true);

// ─── Malignant Hyperthermia Questionnaire ─────────────────────────────────────
export function MhQForm({
  data, isComplete, isEditing, onStartEdit,
  saving, savedOk, saveError,
  onUpdate, onSubmit,
  staffReadOnly = false, onMarkComplete,
}) {
  const isMhAnswered = (q) => {
    const v = data[q.key];
    if (v === undefined || v === null || v === '') return false;
    if (typeof v === 'object') {
      if (v.answer === undefined || v.answer === null) return false;
      if (q.type === 'yesno_explain' && v.answer === true && !(v.explain || '').trim()) return false;
      return true;
    }
    return true;
  };
  const allAnswered = MH_QUESTIONS.every(isMhAnswered);
  const patientDone = isComplete && !isEditing;
  const locked = staffReadOnly || patientDone;

  return (
    <Paper elevation={1} sx={{ p: 3, border: patientDone ? '2px solid #66bb6a' : '1px solid #e0e0e0' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {patientDone && <CheckCircleIcon sx={{ color: 'success.main' }} />}
          <Typography variant="h6" fontWeight={700}>Malignant Hyperthermia Questionnaire</Typography>
          {saving && <CircularProgress size={16} />}
          {savedOk && <Chip label="Saved ✓" size="small" color="success" />}
        </Stack>
        {patientDone && !staffReadOnly && (
          <Button size="small" variant="outlined" onClick={onStartEdit}>Edit</Button>
        )}
        {staffReadOnly && !patientDone && onMarkComplete && Object.keys(data).length > 0 && (
          <Button size="small" variant="outlined" color="success" onClick={onMarkComplete}>
            Mark as Submitted by Patient
          </Button>
        )}
      </Stack>

      {patientDone ? (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1 }}>
          This form has been submitted. Click <strong>Edit</strong> above to make changes.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Please answer the following questions about your personal and family medical history.
        </Typography>
      )}

      {saveError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{saveError}</Alert>}

      {MH_QUESTIONS.map((q, idx) => {
        const raw = data[q.key];
        const isObject = typeof raw === 'object' && raw !== null;
        const answer = isObject ? raw.answer : raw;
        const explain = isObject ? (raw.explain || '') : '';
        const isYes = answer === true;
        return (
          <Box
            key={q.key}
            sx={{
              py: 1.25, px: 1.5,
              borderBottom: idx < MH_QUESTIONS.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              bgcolor: isYes ? '#fff8e1' : 'transparent',
              borderRadius: 1,
              opacity: locked ? 0.85 : 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <RadioGroup
                row
                value={answer === true ? 'yes' : answer === false ? 'no' : ''}
                onChange={(e) => {
                  if (locked) return;
                  const val = e.target.value === 'yes';
                  if (q.type === 'yesno_explain') {
                    onUpdate(q.key, { answer: val, explain: isObject ? explain : '' });
                  } else {
                    onUpdate(q.key, val);
                  }
                }}
                sx={{ flexShrink: 0, mt: -0.25 }}
              >
                <FormControlLabel value="yes" disabled={locked} control={<Radio size="small" sx={{ py: 0.25 }} />} label={<Typography variant="caption" fontWeight={600}>Yes</Typography>} sx={{ mr: 1 }} />
                <FormControlLabel value="no"  disabled={locked} control={<Radio size="small" sx={{ py: 0.25 }} />} label={<Typography variant="caption" fontWeight={600}>No</Typography>} />
              </RadioGroup>
              <Typography variant="body2" sx={{ pt: 0.25, lineHeight: 1.5 }}>{q.text}</Typography>
            </Box>
            {q.type === 'yesno_explain' && isYes && (
              <TextField
                size="small" fullWidth multiline minRows={2}
                placeholder="Please explain..."
                value={explain}
                onChange={(e) => onUpdate(q.key, { answer: true, explain: e.target.value })}
                sx={{ mt: 1, ml: 6 }}
                disabled={locked}
                required
                error={!locked && !explain.trim()}
                helperText={!locked && !explain.trim() ? 'Please provide details for this Yes answer.' : ''}
              />
            )}
          </Box>
        );
      })}

      {!locked && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" disabled={saving || !allAnswered} onClick={onSubmit}>
              {isComplete ? 'Save Changes' : 'Submit Form'}
            </Button>
            {!allAnswered && (
              <Typography variant="caption" color="text.secondary">Please answer all questions before submitting.</Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}

// ─── Responsible Party Info & Release ─────────────────────────────────────────
export function RespPartyForm({
  data, isComplete, isEditing, onStartEdit,
  saving, savedOk, saveError,
  onUpdate, onSubmit,
  staffReadOnly = false, onMarkComplete,
}) {
  const required = ['rp_first_name', 'rp_last_name', 'rp_relationship', 'rp_phone', 'rp_dob'];
  const agreed = data['rp_consent'] === 'agree';
  const phoneVal = data['rp_phone'] || '';
  const phoneValid = phoneVal === '' || /^\d{3}-\d{3}-\d{4}$/.test(phoneVal);
  const dobVal = data['rp_dob'] || '';
  const dobValid = dobVal === '' || /^\d{4}-\d{2}-\d{2}$/.test(dobVal);
  const allFilled = required.every(k => (data[k] || '').trim().length > 0)
    && agreed
    && phoneValid
    && dobValid
    && (data['rp_patient_signature'] || '').trim().length > 0;
  const patientDone = isComplete && !isEditing;
  const locked = staffReadOnly || patientDone;

  const field = (key, label, opts = {}) => (
    <TextField
      key={key}
      size="small"
      label={label}
      value={data[key] || ''}
      onChange={(e) => onUpdate(key, e.target.value)}
      fullWidth={opts.fullWidth}
      sx={{ flex: opts.flex || 1, minWidth: opts.minWidth || 140 }}
      required={required.includes(key)}
      disabled={locked}
    />
  );

  return (
    <Paper elevation={1} sx={{ p: 3, border: patientDone ? '2px solid #66bb6a' : '1px solid #e0e0e0' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {patientDone && <CheckCircleIcon sx={{ color: 'success.main' }} />}
          <Typography variant="h6" fontWeight={700}>Responsible Party Info &amp; Release</Typography>
          {saving && <CircularProgress size={16} />}
          {savedOk && <Chip label="Saved ✓" size="small" color="success" />}
        </Stack>
        {patientDone && !staffReadOnly && (
          <Button size="small" variant="outlined" onClick={onStartEdit}>Edit</Button>
        )}
        {staffReadOnly && !patientDone && onMarkComplete && Object.keys(data).length > 0 && (
          <Button size="small" variant="outlined" color="success" onClick={onMarkComplete}>
            Mark as Submitted by Patient
          </Button>
        )}
      </Stack>

      {patientDone ? (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1, mb: 2 }}>
          This form has been submitted. Click <strong>Edit</strong> above to make changes.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Please provide the information for the person you would like to designate as your responsible party.  This person will be accompanying you from the facility after your procedure and will be getting updates regarding your procedure.
        </Typography>
      )}

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {field('rp_first_name', 'First Name')}
        {field('rp_last_name', 'Last Name')}
        {field('rp_relationship', 'Relationship to Patient')}
        <TextField
          size="small"
          label="Date of Birth"
          type="date"
          value={data['rp_dob'] || ''}
          onChange={(e) => onUpdate('rp_dob', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1, minWidth: 160 }}
          required
          disabled={locked}
        />
        <TextField
          size="small"
          label="Cell Phone Number"
          value={data['rp_phone'] || ''}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
            const fmt = raw.length <= 3 ? raw
              : raw.length <= 6 ? `${raw.slice(0,3)}-${raw.slice(3)}`
              : `${raw.slice(0,3)}-${raw.slice(3,6)}-${raw.slice(6)}`;
            onUpdate('rp_phone', fmt);
          }}
          placeholder="555-867-5309"
          error={!!phoneVal && !phoneValid}
          helperText={!!phoneVal && !phoneValid ? 'Format: xxx-xxx-xxxx' : ''}
          sx={{ flex: 1, minWidth: 160 }}
          required
          disabled={locked}
        />
      </Box>
      {/* Consent */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5, mb: 2,
          borderColor: data['rp_consent'] === 'agree' ? 'success.main' : data['rp_consent'] === 'disagree' ? 'error.main' : 'divider',
          borderWidth: 1.5,
          bgcolor: data['rp_consent'] === 'agree' ? '#f0fdf4' : data['rp_consent'] === 'disagree' ? '#fff5f5' : '#fafafa',
        }}
      >
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
          I give permission for this person to be contacted by the surgery center staff about my procedure on this date &amp; to be my responsible party.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant={data['rp_consent'] === 'agree' ? 'contained' : 'outlined'}
            color="success" size="medium" disabled={locked}
            onClick={() => onUpdate('rp_consent', 'agree')}
            sx={{ fontWeight: 700, minWidth: 130, fontSize: '0.95rem' }}
          >
            ✓ I AGREE
          </Button>
          <Button
            variant={data['rp_consent'] === 'disagree' ? 'contained' : 'outlined'}
            color="error" size="medium" disabled={locked}
            onClick={() => onUpdate('rp_consent', 'disagree')}
            sx={{ fontWeight: 700, minWidth: 130, fontSize: '0.95rem' }}
          >
            ✗ I DISAGREE
          </Button>
        </Stack>
        {data['rp_consent'] === 'disagree' && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            You have indicated disagreement. Please contact our office before proceeding.
          </Alert>
        )}
      </Paper>

      {/* Signature */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: locked ? 1 : 3 }}>
        <TextField
          size="small"
          label="Patient Signature (type full name)"
          sx={{ flex: 2, minWidth: 220 }}
          value={data['rp_patient_signature'] || ''}
          onChange={(e) => onUpdate('rp_patient_signature', e.target.value)}
          disabled={locked}
          required
          helperText="Typing your name constitutes a legal signature"
        />
        <TextField
          size="small" label="Date Signed" type="date"
          sx={{ minWidth: 170 }}
          InputLabelProps={{ shrink: true }}
          value={data['rp_signature_date'] || ''}
          onChange={(e) => onUpdate('rp_signature_date', e.target.value)}
          disabled={locked}
        />
      </Box>

      {!locked && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" disabled={saving || !allFilled} onClick={onSubmit}>
              {isComplete ? 'Save Changes' : 'Submit Form'}
            </Button>
            {!allFilled && (
              <Typography variant="caption" color="text.secondary">
                {!agreed ? 'Please select I AGREE or I DISAGREE above.' : 'Please complete all required fields.'}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}
